// Apolo IDE — local evaluator runner (LM Studio / Gemma 4 E4B).
//
// The Worker aggregates outcomes and gates proposals; the LLM call runs here
// via Electron IPC (free, localhost). Flow: prepare → lmstudio.chat → complete.

import { apiFetch } from './api.js';
import { formatSelfImproveError } from './formatSelfImproveError.js';

export const EVALUATOR_MODEL_ALIAS = 'gemma-4-e4b';
/** LM Studio API id (from `lms load` → "use identifier …"). */
export const EVALUATOR_LMSTUDIO_API_ID = 'google/gemma-4-e4b:2';
/** Short alias for `lms load` and UI; chat resolves to EVALUATOR_LMSTUDIO_API_ID. */
export const EVALUATOR_LOCAL_MODEL = EVALUATOR_MODEL_ALIAS;
export const EVALUATOR_LMSTUDIO_BASE = 'http://localhost:1234/v1';
export const EVALUATOR_SCHEDULE_NAME = 'Aprendizado entre sessões';
export const EVALUATOR_DEFAULT_CRON = '0 4 * * *';

const hasLmStudio = () => typeof window !== 'undefined' && Boolean(window.apolo?.lmstudio?.chat);
const hasOllama = () => typeof window !== 'undefined' && Boolean(window.apolo?.ollama?.chat);

/** Default Ollama model for the evaluator (LOCAL FALLBACK — used only if the cloud call fails). */
export const EVALUATOR_OLLAMA_MODEL = 'qwen2.5-hermes:latest';
export const EVALUATOR_OLLAMA_BASE = 'http://localhost:11434/v1';

/**
 * Default CLOUD model for the evaluator. The evaluator JUDGES craft quality — a weak local
 * model (qwen) makes weak judgments, so we PREFER a strong cloud reasoner.
 * (Pedro's prior decision — keep gemini as the primary judge.)
 */
export const EVALUATOR_CLOUD_MODEL = 'google/gemini-3.1-pro-preview';

/**
 * COST-BENEFIT cloud fallback (Google). When the strong reasoner (pro) is unavailable
 * — rate limit, cost cap, transient 5xx — we try this cheaper GOOGLE cloud tier BEFORE
 * dropping to the (weaker) local engine. Order: pro → flash → local. A flash judgment
 * still beats qwen/gemma-local for craft quality; the local engine is the last resort
 * (offline / no provider key). Both pro and flash return ok:false on "no key", so the
 * chain still degrades cleanly to local.
 */
export const EVALUATOR_CLOUD_FALLBACK_MODEL = 'google/gemini-3-flash-preview';

/**
 * Cloud evaluator chat — routes a single non-streaming completion through the Worker (which owns
 * the adapter + provider key). Matches the engine.chat contract: ({ model, messages, options }) →
 * { ok, content, error }. Returns ok:false (never throws) so the caller can fall back to local.
 */
export async function cloudEvaluatorChat({ model, messages, options } = {}) {
  try {
    const res = await apiFetch('/api/code-ide/sessions/llm/complete', {
      method: 'POST',
      body: { model: model || EVALUATOR_CLOUD_MODEL, messages, options },
    });
    if (!res.ok) return { ok: false, error: res.error || `HTTP ${res.status}` };
    return { ok: res.data?.ok === true, content: res.data?.content || '', error: res.data?.error };
  } catch (e) {
    return { ok: false, error: e?.message || 'cloud evaluator chat failed' };
  }
}

/** The PREFERRED engine: cloud gemini (quality). The call falls back to local if it returns ok:false. */
export function resolveEvaluatorCloudEngine() {
  return { engine: 'gemini', chat: cloudEvaluatorChat, model: EVALUATOR_CLOUD_MODEL, baseUrl: '' };
}

/**
 * Resolve the local evaluator engine. Prefer OLLAMA (always-on, the Tier-S
 * spine — the loop must self-run without a manually-loaded LM Studio model);
 * fall back to LM Studio; null when neither is reachable.
 *
 * @returns {{ engine: 'ollama'|'lmstudio', chat: Function, model: string, baseUrl: string } | null}
 */
export function resolveEvaluatorEngine() {
  if (hasOllama()) {
    return { engine: 'ollama', chat: window.apolo.ollama.chat, model: EVALUATOR_OLLAMA_MODEL, baseUrl: EVALUATOR_OLLAMA_BASE };
  }
  if (hasLmStudio()) {
    return { engine: 'lmstudio', chat: window.apolo.lmstudio.chat, model: EVALUATOR_LOCAL_MODEL, baseUrl: EVALUATOR_LMSTUDIO_BASE };
  }
  return null;
}

export function resolveLocalTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Pick the best text blob from LM Studio (content vs reasoning). */
export function pickEvaluatorModelText(chatRes) {
  if (!chatRes || typeof chatRes !== 'object') return '';
  const content = typeof chatRes.content === 'string' ? chatRes.content.trim() : '';
  const reasoning = typeof chatRes.reasoning === 'string' ? chatRes.reasoning.trim() : '';
  if (content.includes('[')) return content;
  if (reasoning.includes('[')) return reasoning;
  return content || reasoning || '';
}

/**
 * Full local evaluator pipeline. Uses the resolved local engine (Ollama-first,
 * LM Studio fallback) unless an explicit chatFn is injected (tests). Resolves
 * like runEvaluatorRun.
 */
export async function runEvaluatorViaLocal(scope = 'all', { chatFn, baseUrl, model } = {}) {
  try {
    const prep = await apiFetch('/api/code-ide/evaluator/prepare', { method: 'POST', body: { scope } });
    if (!prep.ok) {
      return {
        proposals: [],
        considered: 0,
        error: formatSelfImproveError(prep.error || `HTTP ${prep.status}`, { status: prep.status }),
      };
    }
    const { system, user, considered = 0, gated_count = 0, model_id: prepModel } = prep.data || {};
    if (gated_count === 0) {
      return { proposals: [], considered, gated_count, parsed_count: 0 };
    }

    const messages = [
      { role: 'system', content: system },
      { role: 'user', content: user },
    ];
    const callOpts = { temperature: 0.2, max_tokens: 2048 };

    // Engine selection: an injected chatFn (tests) is used verbatim. In production the chain is
    // pro → flash → local: PREFER the strong cloud reasoner (gemini-3.1-pro), then the cheaper
    // GOOGLE cloud tier (gemini-3-flash) for cost-benefit when pro is unavailable, then the LOCAL
    // engine (Ollama/LM Studio) as the last resort (offline / no provider key). Never breaks.
    let chatRes;
    if (chatFn) {
      chatRes = await chatFn({
        baseUrl: baseUrl || EVALUATOR_LMSTUDIO_BASE,
        model: model || EVALUATOR_LOCAL_MODEL,
        messages,
        options: callOpts,
      });
    } else {
      const cloud = resolveEvaluatorCloudEngine();
      chatRes = await cloud.chat({ model: model || cloud.model, messages, options: callOpts });
      // Tier 2 (cost-benefit Google): cheaper cloud reasoner before the weaker local engine.
      // Skipped when the caller pinned an explicit `model` (override is honored as-is).
      if (!chatRes?.ok && !model) {
        chatRes = await cloud.chat({ model: EVALUATOR_CLOUD_FALLBACK_MODEL, messages, options: callOpts });
      }
      // Tier 3 (local last resort): only when both cloud tiers are unavailable.
      if (!chatRes?.ok) {
        const local = resolveEvaluatorEngine();
        if (local?.chat) {
          chatRes = await local.chat({
            baseUrl: local.baseUrl,
            model: local.model || prepModel || EVALUATOR_LOCAL_MODEL,
            messages,
            options: callOpts,
          });
        }
      }
    }

    if (!chatRes?.ok) {
      return {
        proposals: [],
        considered,
        gated_count,
        parsed_count: 0,
        model_error: chatRes?.error || 'Motor do evaluator indisponível (cloud + local falharam)',
      };
    }

    const modelText = pickEvaluatorModelText(chatRes);

    const complete = await apiFetch('/api/code-ide/evaluator/complete', {
      method: 'POST',
      body: { scope, model_output: modelText, allow_fallback: true },
    });
    if (!complete.ok) {
      return {
        proposals: [],
        considered,
        gated_count,
        parsed_count: 0,
        error: formatSelfImproveError(complete.error || `HTTP ${complete.status}`, { status: complete.status }),
      };
    }

    return {
      proposals: complete.data?.proposals || [],
      considered: complete.data?.considered ?? considered,
      gated_count: complete.data?.gated_count ?? gated_count,
      parsed_count: complete.data?.parsed_count ?? 0,
      fallback_used: Boolean(complete.data?.fallback_used),
    };
  } catch (e) {
    return {
      proposals: [],
      considered: 0,
      error: formatSelfImproveError(e?.message || String(e)),
    };
  }
}

/** @deprecated Back-compat alias — the runner now prefers Ollama. Use runEvaluatorViaLocal. */
export const runEvaluatorViaLmStudio = runEvaluatorViaLocal;

/** Stash proposals for the LearnedRules toast (scheduled runs). */
export async function stashEvaluatorProposals(proposals, { considered = 0 } = {}) {
  if (!Array.isArray(proposals) || proposals.length === 0) return { stashed: false };
  try {
    const res = await apiFetch('/api/code-ide/evaluator/pending/stash', {
      method: 'POST',
      body: { proposals, considered },
    });
    return { stashed: Boolean(res.ok && res.data?.stashed), count: res.data?.count ?? proposals.length };
  } catch {
    return { stashed: false };
  }
}

/** Patch schedule timezone on the builtin evaluator task (best-effort). */
export async function ensureEvaluatorScheduleTimezone() {
  const engine = resolveEvaluatorEngine();
  if (!engine) return { ok: false };
  try {
    const res = await apiFetch('/api/code-ide/schedule');
    if (!res.ok) return { ok: false };
    const tasks = res.data?.tasks || [];
    const task = tasks.find((t) => t.name === EVALUATOR_SCHEDULE_NAME);
    if (!task) return { ok: false, reason: 'missing' };
    const tz = resolveLocalTimezone();
    let payload = {};
    try { payload = JSON.parse(task.command_payload || '{}'); } catch { payload = {}; }
    const needsCron = task.cron_expression === '0 7 * * *';
    const needsTz = payload.timezone !== tz;
    if (!needsCron && !needsTz) return { ok: true, updated: false, timezone: tz };
    await apiFetch(`/api/code-ide/schedule/${task.id}`, {
      method: 'PATCH',
      body: {
        ...(needsCron ? { cron_expression: EVALUATOR_DEFAULT_CRON } : {}),
        command_payload: { ...payload, timezone: tz, execution_target: 'local', model_id: engine.model },
      },
    });
    return { ok: true, updated: true, timezone: tz, cron: needsCron ? EVALUATOR_DEFAULT_CRON : task.cron_expression };
  } catch {
    return { ok: false };
  }
}
